import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import EmptyState from '~/modules/components/global/empty-state';
import { Input } from '~/components/ui/input';
import {
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '~/components/ui/select';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '~/components/ui/table';
import { Label } from '~/components/ui/label';
import confirm from '~/modules/components/global/confirm';
import ToastService from '~/modules/services/toast-service';
import ServiceAccountService from '~/modules/services/service-account-service';
import ApiTokenRevealModal from './api-token-reveal-modal';
import { captureError } from '../../../pixwel/exception-handler.factory';

const EXPIRY_OPTIONS = [
	{ label: '30 days', value: 30 },
	{ label: '60 days', value: 60 },
	{ label: '90 days', value: 90 },
	{ label: '180 days', value: 180 },
	{ label: '1 year', value: 365 }
];

const formatDate = (timestamp) => {
	if(!timestamp) {
		return '—';
	}
	return new Date(timestamp * 1000).toLocaleString();
};

const expiresAtFromDays = (days) => Math.floor(Date.now() / 1000) + days * 24 * 60 * 60;

const ServiceAccountTokens = ({ account }) => {
	const queryClient = useQueryClient();
	const queryKey = ['serviceAccountTokens', account._id];

	const [name, setName] = useState('');
	const [expiryDays, setExpiryDays] = useState(90);
	const [revealedToken, setRevealedToken] = useState(null);

	const { data: tokens = [], isLoading } = useQuery(
		queryKey,
		() => ServiceAccountService.listTokens(account._id),
		{ refetchOnWindowFocus: false }
	);

	const { mutateAsync: issueToken, isLoading: isIssuing } = useMutation(
		({ name, expiresAt }) => ServiceAccountService.issueToken(account._id, { name, expiresAt }),
		{
			onSuccess: (token) => {
				queryClient.invalidateQueries(queryKey);
				setRevealedToken(token);
				setName('');
				setExpiryDays(90);
			},
			onError: (err) => {
				const message = err?.response?.data?.error || err.message || 'Failed to issue token';
				ToastService.create('error', message);
				captureError(err, `Error issuing service account token: ${err.message}`);
			}
		}
	);

	const { mutateAsync: revokeToken } = useMutation(
		(tokenId) => ServiceAccountService.revokeToken(account._id, tokenId),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(queryKey);
				ToastService.create('success', 'Token revoked');
			},
			onError: (err) => {
				ToastService.create('error', err?.response?.data?.error || 'Failed to revoke token');
				captureError(err, `Error revoking service account token: ${err.message}`);
			}
		}
	);

	const handleIssue = async() => {
		const trimmed = name.trim();
		if(!trimmed) {
			return;
		}
		await issueToken({ name: trimmed, expiresAt: expiresAtFromDays(expiryDays) });
	};

	const handleRevoke = async(row) => {
		// Named deliberately: this is shared infrastructure, and whoever revokes it may not be the
		// person who deployed whatever is using it.
		const ok = await confirm({
			title: 'Revoke service account token',
			content: `Revoke "${row.name}" on ${account.name}? Any integration using this token will `
				+ 'stop working immediately.',
			confirmText: 'Revoke',
			confirmVariant: 'danger',
			cancelText: 'Cancel',
			cancelVariant: 'secondary'
		});
		if(!ok) {
			return;
		}
		await revokeToken(row._id);
	};

	return (
		<div data-testid={`service-account-tokens-${account._id}`}>
			{!account.disabledAt && (
				<div className="flex gap-3 items-end flex-wrap mb-4">
					<div className="flex flex-col gap-1.5 flex-1 min-w-[14rem]">
						<Label htmlFor={`service-account-token-name-${account._id}`}>Token name</Label>
						<Input
							id={`service-account-token-name-${account._id}`}
							placeholder="e.g. nightly-sync"
							value={name}
							onChange={(e) => setName(e.target.value)}
							data-testid="service-account-token-name-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5 w-36">
						<Label>Expires in</Label>
						<Select value={String(expiryDays)} onValueChange={(val) => setExpiryDays(Number(val))}>
							<SelectTrigger data-testid="service-account-token-expiry-select">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{EXPIRY_OPTIONS.map((opt) => (
									<SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<Button
						onClick={handleIssue}
						loading={isIssuing}
						disabled={!name.trim() || isIssuing}
						data-testid="service-account-token-issue-button"
					>
						Issue token
					</Button>
				</div>
			)}
			<p className="text-sm text-muted-foreground mb-4">
				To rotate without downtime: issue a new token, deploy it, then revoke the old one.
			</p>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Name</TableHead>
						<TableHead>Token</TableHead>
						<TableHead>Created</TableHead>
						<TableHead>Last used</TableHead>
						<TableHead>Expires</TableHead>
						<TableHead></TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{isLoading
						? (
							<TableRow>
								<TableCell colSpan={6} className="text-center text-muted-foreground">Loading…</TableCell>
							</TableRow>
						)
						: tokens.length === 0
							? (
								<TableRow>
									<TableCell colSpan={6}>
										<EmptyState message="No active tokens" />
									</TableCell>
								</TableRow>
							)
							: (
								tokens.map((token) => (
									<TableRow key={token._id}>
										<TableCell className="font-medium max-w-[10rem] truncate">{token.name}</TableCell>
										<TableCell>
											<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">
												svc_{token.tokenPrefix}…
											</code>
										</TableCell>
										<TableCell className="text-muted-foreground text-sm">{formatDate(token.createdAt)}</TableCell>
										<TableCell className="text-muted-foreground text-sm">{formatDate(token.lastUsedAt)}</TableCell>
										<TableCell className="text-muted-foreground text-sm">{formatDate(token.expiresAt)}</TableCell>
										<TableCell className="text-right">
											<Button
												variant="danger"
												data-testid="service-account-token-revoke-button"
												onClick={() => handleRevoke(token)}
											>
												<Icon use="trash" color="white" /> Revoke
											</Button>
										</TableCell>
									</TableRow>
								))
							)}
				</TableBody>
			</Table>
			<ApiTokenRevealModal
				token={revealedToken}
				onClose={() => setRevealedToken(null)}
				title="Service account token created"
				scopeWarning={`Anyone with this value can act as ${account.name} on the API.`}
			/>
		</div>
	);
};

ServiceAccountTokens.propTypes = {
	account: PropTypes.shape({
		_id: PropTypes.string.isRequired,
		name: PropTypes.string,
		disabledAt: PropTypes.number
	}).isRequired
};

export default ServiceAccountTokens;
